import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import styled from '@emotion/styled';
import { v4 as uuid } from 'uuid';

import CONST from '../constants';
import { Container, Title, Body } from './Common';
import imgFloor1F from '../resources/floor-plan-1f.jpg';
import imgFloor6F from '../resources/floor-plan-6f.jpg';
import imgFloor7F from '../resources/floor-plan-7f.jpg';
import imgFloor815F from '../resources/floor-plan-8-15f.jpg';
import imgFloor16F from '../resources/floor-plan-16f.jpg';
import imgFloor17F from '../resources/floor-plan-17f.jpg';
import imgFloor18F from '../resources/floor-plan-18f.jpg';

const BREAKPOINT_LIMIT = CONST.SCREEN.BREAKPOINT.XL;

const StyledRow = styled(Row)`
    margin: 0;
    flex-direction: column-reverse;
    justify-content: space-around;
`;

const ImageCol = styled(Col)`
    position: relative;
    flex: 0 0 auto;
    width: 100%;
    max-width: 1040px;

    @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
        width: 70%;
        margin: 0 auto;
    }

    & > div {
        &.animation-enter {
            opacity: 0;
            transform: translateX(-75%);
        }
        &.animation-enter-active {
            opacity: 1;
            transform: translateX(0);
            transition: all 500ms ease;
        }
    }
`;

const FilterContainer = styled.div`
    display: block;
    position: relative;

    & > a {
        display: inline-block;
        margin: 0;
        padding: 0 2.5vw;
        border-right: 0.75vw solid ${CONST.COLOR.THEME_SECONDARY};
        color: ${CONST.COLOR.THEME_SECONDARY} !important;
        cursor: pointer;
        font-size: 5vw;
        font-weight: bold;
        text-decoration: none;

        &:last-of-type {
            border: none;
        }

        @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
            padding: 0 ${CONST.SCREEN.BREAKPOINT.SM * 0.045}px;
            border-right: 0.25vw solid ${CONST.COLOR.TEXT};
            font-size: 2.5vw;
        }

        @media screen and (min-width: ${BREAKPOINT_LIMIT}px) {
            padding: 0 40px;
            border-right: ${BREAKPOINT_LIMIT * 0.0025}px solid ${CONST.COLOR.TEXT};
            font-size: ${BREAKPOINT_LIMIT * 0.025}px;
        }

        &:hover {
            color: ${CONST.COLOR.THEME} !important;
        }

        &.active {
            color: ${CONST.COLOR.THEME} !important;

            @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
                &::before {
                    position: absolute;
                    content: '•';
                    transform: translate(-150%, -10%);
                }
            }
        }
    }
`;

const FloorContainer = styled.div`
    position: relative;
    margin-top: 10vw;
    text-align: center;

    @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
        margin-top: 5vw;
    }

    @media screen and (min-width: ${BREAKPOINT_LIMIT}px) {
        margin-top: ${BREAKPOINT_LIMIT * 0.1}px;
    }

    &:first-of-type {
        margin-top: 5vw;

        @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
            margin-top: 3vw;
        }

        @media screen and (min-width: ${BREAKPOINT_LIMIT}px) {
            margin-top: ${BREAKPOINT_LIMIT * 0.03}px;
        }
    }

    & > p {
        display: inline-block;
        margin: 0;
        color: ${CONST.COLOR.THEME};
        font-size: 10vw;
        font-weight: bold;
        line-height: 1.25;
        text-transform: uppercase;

        @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
            font-size: 5vw;
        }

        @media screen and (min-width: ${BREAKPOINT_LIMIT}px) {
            font-size: ${BREAKPOINT_LIMIT * 0.05}px;
        }
    }
`;

const ImageContainer = styled.div`
    display: block;
    position: relative;
    width: 100%;
    margin: 0 auto;

    @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
        border-radius: 0.5vw;
    }

    @media screen and (min-width: ${BREAKPOINT_LIMIT}px) {
        border-radius: ${BREAKPOINT_LIMIT * 0.005}px;
    }

    & > img {
        width: 100%;
        border-radius: inherit;
        box-shadow: 0 0.25vw 0.5vw ${CONST.COLOR.BG};
    }

    & > div {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        border: 0.2vw solid ${CONST.COLOR.THEME};
        border-radius: inherit;

        @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
            border: 0.2vw solid ${CONST.COLOR.THEME}BD;
        }

        @media screen and (min-width: ${BREAKPOINT_LIMIT}px) {
            border: ${BREAKPOINT_LIMIT * 0.002}px solid ${CONST.COLOR.THEME}BD;
        }
    }
`;

const ImageBadge = styled.div`
    position: absolute;
    top: -10vw;
    right: 1vw;
    margin: 0;
    padding: 1vw 2vw;
    background: ${CONST.COLOR.THEME_LIGHT};
    border-radius: 2vw;
    color: #fff;

    @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
        top: -5vw;
        right: 0;
        padding: 0.5vw 0.8vw;
        border-radius: 0.6vw;
    }

    @media screen and (min-width: ${BREAKPOINT_LIMIT}px) {
        top: -60px;
        right: 0;
        padding: 6px 10px;
        border-radius: 6px;
    }

    & > .left {
        display: inline-block;
        margin: 0;
        padding: 1vw;
        background: ${CONST.COLOR.CREAM}7F;
        color: ${CONST.COLOR.THEME};
        border-radius: 1.5vw;
        font-size: 3vw;
        font-weight: bold;
        vertical-align: middle;

        @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
            padding: 0.5vw;
            border-radius: 0.5vw;
            font-size: 1.25vw;
        }

        @media screen and (min-width: ${BREAKPOINT_LIMIT}px) {
            padding: ${BREAKPOINT_LIMIT * 0.005}px;
            border-radius: ${BREAKPOINT_LIMIT * 0.005}px;
            font-size: ${BREAKPOINT_LIMIT * 0.0125}px;
        }
    }

    & > .right {
        display: inline-block;
        margin: 0 0 0 1.5vw;
        color: ${CONST.COLOR.CREAM};
        font-size: 4vw;
        font-weight: bold;
        vertical-align: middle;

        @media screen and (min-width: ${CONST.SCREEN.BREAKPOINT.SM}px) {
            margin-left: 0.5vw;
            font-size: 1.75vw;
        }

        @media screen and (min-width: ${BREAKPOINT_LIMIT}px) {
            margin-left: ${BREAKPOINT_LIMIT * 0.005}px;
            font-size: 20px;
        }
    }
`;

const FloorData = {
    0: [{ id: uuid(), image: imgFloor1F, text: '1F' }],
    1: [{ id: uuid(), image: imgFloor6F, text: '6F' }],
    2: [{ id: uuid(), image: imgFloor7F, text: '7F' }],
    3: [{ id: uuid(), image: imgFloor815F, text: '8~15F' }],
    4: [{ id: uuid(), image: imgFloor16F, text: '16F' }],
    5: [{ id: uuid(), image: imgFloor17F, text: '17F' }],
    6: [{ id: uuid(), image: imgFloor18F, text: '18F' }],
};

const FloorPlan = () => {
    const { hash } = useLocation();
    const activeNav = hash === '#1f' ? 0 : hash === '#6f' ? 1 : hash === '#7f' ? 2 : hash === '#8~15f' ? 3 : hash === '#16f' ? 4 : hash === '#17f' ? 5 : hash === '#18f' ? 6 : 0;
    const [floors, setFloors] = useState(FloorData[activeNav]);

    const handleClick = (index) => {
        setFloors(FloorData[index]);
        window.scrollTo(0, 0);
    };

    return (
        <Container>
            <Title textKr="층별안내" textEng="Floor Plan" />
            <Body className="mt-3 mt-sm-4" style={{ maxWidth: 'none' }}>
                <StyledRow className="g-0">
                    <ImageCol className="g-0">
                        <TransitionGroup component={null}>
                            <CSSTransition key={activeNav} timeout={500} classNames="animation" exit={false}>
                                <div>
                                    {floors.map(({ id, image, text }) => (
                                        <FloorContainer key={id}>
                                            <p>{text}</p>
                                            <ImageContainer>
                                                <img src={image} alt="" />
                                                <div>
                                                    {text !== '' && (
                                                        <ImageBadge>
                                                            <p className="left">상담문의</p>
                                                            <p className="right">{CONST.PHONE}</p>
                                                        </ImageBadge>
                                                    )}
                                                </div>
                                            </ImageContainer>
                                        </FloorContainer>
                                    ))}
                                </div>
                            </CSSTransition>
                        </TransitionGroup>
                    </ImageCol>
                    <Col className="g-0 col-auto">
                        <FilterContainer>
                            <a href="#1f" className={activeNav === 0 ? 'active' : null} onClick={() => handleClick(0)}>
                                1F
                            </a>
                            <a href="#6f" className={activeNav === 1 ? 'active' : null} onClick={() => handleClick(1)}>
                                6F
                            </a>
                            <a href="#7f" className={activeNav === 2 ? 'active' : null} onClick={() => handleClick(2)}>
                                7F
                            </a>
                            <a href="#8~15f" className={activeNav === 3 ? 'active' : null} onClick={() => handleClick(3)}>
                                8~15F
                            </a>
                            <a href="#16f" className={activeNav === 4 ? 'active' : null} onClick={() => handleClick(4)}>
                                16F
                            </a>
                            <a href="#17f" className={activeNav === 5 ? 'active' : null} onClick={() => handleClick(5)}>
                                17F
                            </a>
                            <a href="#18f" className={activeNav === 6 ? 'active' : null} onClick={() => handleClick(6)}>
                                18F
                            </a>
                        </FilterContainer>
                    </Col>
                </StyledRow>
            </Body>
        </Container>
    );
};

export default FloorPlan;
